import { MANAGE_ALL_ACCOUNTS_PERMISSION, PRODUCT_GROUPS } from '@headline/schemas'
import { useSuspenseQuery } from '@tanstack/react-query'
import { Link, createFileRoute, redirect } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { PageHeader } from '@/components/PageHeader'
import { RestrictedBadge } from '@/components/RestrictedBadge'
import { WorkspaceLauncher } from '@/components/WorkspaceLauncher'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { accountsQuery, myWorkspacesQuery, sessionQuery, useCreateAccount } from '@/lib/queries'
import { isStaff } from '@/lib/viewer'

/** The delivery products (Reports / Library / RTA / Analytics) in catalogue order, for the accounts
 *  table's Products column — labelled from the shared catalogue so a rename lands in one place. */
const DELIVERY_PRODUCTS = PRODUCT_GROUPS.find((group) => group.id === 'delivery')?.features ?? []

/**
 * The launcher, forked by the LEVEL of access — not by whether you're staff.
 *
 * Account-level access gets the account-first Accounts list: staff see every customer, and a
 * customer granted ON an account (view / edit / manage-access) sees the ones they hold, entering
 * each at its full surface — Reports, Topics, sub-accounts & settings, access. Only a user with NO
 * visible account — access granted on a sub-account ALONE — gets the workspace-first launcher, since
 * the parent account is invisible to them and an account-first page would strand them on "No
 * accounts yet".
 *
 * An earlier version forked on staff-ness, which wrongly funnelled an account-level customer into a
 * single sub-account's topic workspace, where only Topics is reachable.
 *
 * Resolved in the loader, and a single account (or single sub-account workspace) is opened straight
 * away — one place is not a choice worth rendering.
 */
export const Route = createFileRoute('/app/accounts/')({
  loader: async ({ context }) => {
    const session = await context.queryClient.ensureQueryData(sessionQuery)
    // Staff get the cross-account admin list — the one place that browses every customer.
    if (isStaff(session.user)) return
    // A customer's TOP-LEVELS: each account they can see (entered at its Home), plus each
    // sub-account they hold WITHOUT its parent account (entered at the client's Home). Combining
    // both is what keeps a customer with access to an account AND a stray client of another from
    // being stranded — the account-only path used to drop the client.
    const [{ accounts }, { workspaces }] = await Promise.all([
      context.queryClient.ensureQueryData(accountsQuery),
      context.queryClient.ensureQueryData(myWorkspacesQuery),
    ])
    const visibleAccounts = new Set(accounts.map((account) => account.id))
    const subOnly = workspaces.filter((workspace) => !visibleAccounts.has(workspace.accountId))
    // Exactly one place to be → go straight there; a single workspace is not a choice worth
    // rendering. Otherwise the launcher lists what they hold (scoped by the API, never all accounts).
    if (accounts.length + subOnly.length === 1) {
      if (accounts.length === 1) {
        throw redirect({ to: '/app/accounts/$accountId', params: { accountId: accounts[0]!.id } })
      }
      const workspace = subOnly[0]!
      throw redirect({
        to: '/app/accounts/$accountId/subaccounts/$subaccountId',
        params: { accountId: workspace.accountId, subaccountId: workspace.subaccountId },
      })
    }
  },
  component: AccountsIndex,
})

function AccountsIndex() {
  const { data: session } = useSuspenseQuery(sessionQuery)
  // Staff get the admin table (browse + create every account); every customer gets the scoped
  // launcher of only the workspaces they hold — never the cross-account browser.
  if (isStaff(session.user)) return <AdminAccounts />
  return <WorkspaceLauncher />
}

function AdminAccounts() {
  const { data } = useSuspenseQuery(accountsQuery)
  const { data: session } = useSuspenseQuery(sessionQuery)
  const [search, setSearch] = useState('')
  const [creating, setCreating] = useState(false)
  const q = search.trim().toLowerCase()
  const shown = q ? data.accounts.filter((a) => a.name.toLowerCase().includes(q)) : data.accounts

  // Creating an account is a Clipbook-staff act server-side (requireClipbookStaff). Showing the
  // control to everyone meant a viewer could type a name, submit, and be told 403.
  const canCreate = isStaff(session.user)

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Accounts"
        description="Each account acts as a workspace. Open a workspace to access its products, and the resources they use. You see the ones you have access to."
      />

      {/* One search bar, the page's main control; creating is a button beside it that opens a
          dialog. The two used to be two look-alike inputs (Kevin, 2026-09-30). */}
      <div className="flex flex-wrap items-center gap-2">
        <Input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search accounts…"
          aria-label="Search accounts"
          className="h-9 max-w-md min-w-64 flex-1"
        />
        {canCreate && (
          <>
            <Button onClick={() => setCreating(true)}>New account</Button>
            <RestrictedBadge who="Clipbook admins" permission={MANAGE_ALL_ACCOUNTS_PERMISSION} />
          </>
        )}
      </div>

      {creating && <NewAccountDialog onClose={() => setCreating(false)} />}

      {data.accounts.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No accounts yet. Creating one also creates its first sub-account.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            {/*
              No "Registered" column: whether an account exists as an FGA resource is plumbing,
              and it read as a status customers were meant to act on (2026-08-25 tech sync). The
              fact still does its job where it means something — it gates "Add someone" on the
              account page, which is the one place an unregistered account actually stops you.
            */}
            <thead className="bg-muted/50">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Account</th>
                <th className="px-3 py-2 text-left font-medium">Sub-accounts</th>
                <th className="px-3 py-2 text-left font-medium">Topics</th>
                <th className="px-3 py-2 text-left font-medium">Products</th>
                <th className="px-3 py-2 text-left font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((account) => {
                // "Products" is the enabled DELIVERY products (Reports / Library / RTA / Analytics) —
                // the delivery shelf, not the data-source entitlements, which are a separate axis.
                // Shown as a badge each, in catalogue order, so the column says WHICH products the
                // account has rather than an opaque count.
                const products = DELIVERY_PRODUCTS.filter((product) =>
                  account.entitlements.includes(product.feature),
                )
                return (
                  <tr
                    key={account.id}
                    className="border-t border-border transition-colors hover:bg-accent/30"
                  >
                    <td className="px-3 py-2">
                      <Link
                        to="/app/accounts/$accountId"
                        params={{ accountId: account.id }}
                        className="font-medium transition-colors hover:text-primary"
                      >
                        {account.name}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-muted-foreground tabular-nums">
                      {/* Client count (non-primary). A solo account reads 0 → shown as a dash, since
                          the level is invisible until the account actually has a client. */}
                      {account.subaccountCount > 0 ? account.subaccountCount : '—'}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground tabular-nums">
                      {account.topicCount && account.topicCount > 0 ? account.topicCount : '—'}
                    </td>
                    <td className="px-3 py-2">
                      {products.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {products.map((product) => (
                            <Badge
                              key={product.feature}
                              variant="secondary"
                              className="font-normal"
                            >
                              {product.label}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {/* Status reads as colour, not another grey pill: a live account is a quiet
                          green, a paused one steps back to neutral. */}
                      <Badge variant={account.status === 'active' ? 'success' : 'neutral'}>
                        {account.status}
                      </Badge>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

/** Name it, create it — the account's first sub-account comes with it. */
function NewAccountDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient()
  const createAccount = useCreateAccount(queryClient)
  const [name, setName] = useState('')
  const submit = () => {
    if (!name.trim() || createAccount.isPending) return
    createAccount.mutate({ name: name.trim() }, { onSuccess: onClose })
  }
  return (
    <Dialog
      open
      onClose={onClose}
      title="New account"
      description="Creating an account also creates its first sub-account."
      body={
        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault()
            submit()
          }}
        >
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-foreground">Account name</span>
            <Input
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="e.g. Acme Corp"
              aria-label="New account name"
            />
          </label>
          {createAccount.isError && (
            <p role="alert" className="text-sm text-destructive">
              {createAccount.error.message}
            </p>
          )}
        </form>
      }
    >
      <Button variant="outline" size="sm" onClick={onClose}>
        Cancel
      </Button>
      <Button size="sm" disabled={createAccount.isPending || !name.trim()} onClick={submit}>
        {createAccount.isPending ? 'Creating…' : 'Create account'}
      </Button>
    </Dialog>
  )
}
